import { assign, sendTo, type ActionArgs as XStateActionArgs } from 'xstate';

import { storeCsrfToken } from '../storage';
import { getSessionRetryDelay } from './delays';
import { logger } from './logger';

import type { AuthEvent } from './events';
import type {
  AuthContext,
  PersistCsrfTokenParams,
  SessionFailureParams,
  StoreCurrentCustomerDataParams,
  StorePostLoginRedirectHrefParams,
} from './types';

// ─────────────────────────────────────────────────────────────────────────────

const broadcastLogInEvent: AuthMachineAction = sendTo('authBroadcast', {
  type: 'BROADCAST_LOG_IN',
});

const broadcastLogOutEvent: AuthMachineAction = sendTo('authBroadcast', {
  type: 'BROADCAST_LOG_OUT',
});

const persistCsrfToken: AuthMachineAction<PersistCsrfTokenParams> = (_args, params) => {
  if (!params.csrfToken) {
    return;
  }

  storeCsrfToken(params.csrfToken);
};

const logSessionFailure: AuthMachineAction<SessionFailureParams> = ({ context }, params) => {
  logger.error('Session Fetch Failed', {
    error: normalizeError(params.error).message,
    failureCount: context.sessionFailureCount,
  });
};

const storeCurrentCustomerData: AuthMachineAction<StoreCurrentCustomerDataParams> = assign(
  (_args, params) => {
    return {
      currentCustomer: params.currentCustomer,
    };
  },
);

const storeCustomerFetchFailure: AuthMachineAction = assign({
  currentCustomer: null,
});

const storeSessionFailure: AuthMachineAction<SessionFailureParams> = assign(
  ({ context }, params) => {
    return {
      sessionError: normalizeError(params.error),
      sessionFailureCount: context.sessionFailureCount + 1,
    };
  },
);

const storeSessionRetryAt: AuthMachineAction = assign(({ context }) => {
  return {
    sessionRetryAt: Date.now() + getSessionRetryDelay(context.sessionFailureCount),
  };
});

const storePostLoginRedirectHref: AuthMachineAction<StorePostLoginRedirectHrefParams> = assign(
  (_args, params) => {
    return { postLoginRedirectHref: params.postLoginRedirectHref };
  },
);

const storeFirstAuthAttemptDone: AuthMachineAction = assign({
  isFirstAuthAttempt: false,
});

const storeHasLoggedOut: AuthMachineAction = assign({
  hasLoggedOut: true,
});

const clearCurrentCustomerData: AuthMachineAction = assign({
  currentCustomer: null,
});

const clearHasLoggedOut: AuthMachineAction = assign({
  hasLoggedOut: false,
});

const clearPostLoginRedirectHref: AuthMachineAction = assign({
  postLoginRedirectHref: undefined,
});

const clearSessionError: AuthMachineAction = assign({
  sessionError: undefined,
});

const clearSessionRetryAt: AuthMachineAction = assign({
  sessionRetryAt: undefined,
});

// ─────────────────────────────────────────────────────────────────────────────

export const AuthMachineActions = {
  broadcastLogInEvent,
  broadcastLogOutEvent,
  logSessionFailure,
  persistCsrfToken,
  storeCurrentCustomerData,
  storeCustomerFetchFailure,
  storeFirstAuthAttemptDone,
  storeHasLoggedOut,
  storePostLoginRedirectHref,
  storeSessionFailure,
  storeSessionRetryAt,
  clearCurrentCustomerData,
  clearHasLoggedOut,
  clearPostLoginRedirectHref,
  clearSessionError,
  clearSessionRetryAt,
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function normalizeError(error: unknown) {
  if (error instanceof Error) {
    return error;
  }

  return new Error(String(error));
}

// ─── Types ───────────────────────────────────────────────────────────────────

type AuthMachineAction<ActionParams extends Record<string, unknown> | void = void> = (
  actionArgs: XStateActionArgs<AuthContext, AuthEvent, AuthEvent>,
  params: ActionParams,
) => void;
